import React, { useContext, useEffect, useRef, useState } from "react";
import { CircularProgress, Backdrop } from "@mui/material";
import Swal from "sweetalert2";
import MDTypography from "components/MDTypography";
import MDBox from "components/MDBox";
import { UserContext, notify } from "App";
import colors from "assets/theme/base/colors";
import {
  FindDevoteeAPI,
  GetDevoteeDataAPI,
  GetDevoteeAddressAPI,
  AddDevoteeAPI,
  EditDevoteDataAPI,
  EditDevoteeAddressAPI,
  AddAddressAPI,
} from "APIs/DevoteeAPIs";
import { AddReceiptAPI } from "APIs/ReceiptAPI";
import { GetCountryCodeList } from "APIs/CommonAPI";
import DevoteeSearch from "./components/DevoteeSearch";
import DevoteeForm from "./components/DevoteeForm";
import AddressSearch from "./components/AddressSearch";
import AddressForm from "./components/AddressForm";
import ReceiptForm from "./components/ReceiptForm";
import SectionCard from "./components/SectionCard";
import ReceiptHeader from "./components/ReceiptHeader";

/* -------------------------------------------------------------------------- */
/* Initial state                                                              */
/* -------------------------------------------------------------------------- */
const initialDevoteeData = {
  first_name: "",
  middle_name: "",
  last_name: "",
  email: "",
  phone: "",
  gender: null,
  birth_date: "",
  anniversary_date: "",
  comments: "",
};

const initialAddressData = {
  title: "",
  phone: "",
  address_line_1: "",
  address_line_2: "",
  city: "",
  state: "",
  country: "",
  pincode: "",
  is_default: 0,
};

const initialCountryCode = {
  value: 112,
  label: "INDIA  :  +91",
};

/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */
// Server format is dd-mm-yyyy, <input type="date"> uses yyyy-mm-dd. The swap is symmetric.
const flipDate = (value) => value?.split("-").reverse().join("-");

// Server devotee -> form devotee
const toDevoteeForm = (data) => ({
  ...data,
  birth_date: flipDate(data.birth_date),
  anniversary_date: flipDate(data.anniversary_date),
});

// Drops empty values so they are not sent to the API
const cleanPayload = (obj) =>
  Object.fromEntries(
    Object.entries(obj).filter(([, value]) => value !== null && value !== undefined && value !== "")
  );

// Turns the text typed in the search box into values for the new devotee form:
//   digits only        -> phone
//   one word           -> first name
//   two or more words  -> first name + last name (words in between go to middle name)
const capitalizeWord = (word) => word.charAt(0).toUpperCase() + word.slice(1);

const getPrefillFromSearch = (text) => {
  const value = (text || "").trim();
  if (!value) return {};

  // Only digits (and + - ( ) spaces): a phone number, keep the last 10 digits
  if (/^[\d\s+\-()]+$/.test(value)) {
    const digits = value.replace(/\D/g, "");
    return digits ? { phone: digits.slice(-10) } : {};
  }

  // Only letters and spaces: a name
  if (/^[\p{L}\s.'-]+$/u.test(value)) {
    const words = value.split(/\s+/).map(capitalizeWord);
    if (words.length === 1) return { first_name: words[0] };
    return {
      first_name: words[0],
      last_name: words[words.length - 1],
      ...(words.length > 2 && { middle_name: words.slice(1, -1).join(" ") }),
    };
  }

  // Mixed text (letters + digits): nothing to prefill
  return {};
};

/* -------------------------------------------------------------------------- */
/* Component                                                                  */
/* -------------------------------------------------------------------------- */
const Receipt = () => {
  const userData = useContext(UserContext);
  const token = userData.access_token;

  // isLoading = full screen loader (save / select / send receipt only)
  const [isLoading, setIsLoading] = useState(false);
  // isSearching = only the small "Searching..." text inside the search dropdown
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState(null);

  // Only the answer of the latest search may update the list (typing fast)
  const latestSearch = useRef(0);

  // Devotee related state
  const [devoteeOptions, setDevoteeOptions] = useState([]);
  const [isNewDevotee, setIsNewDevotee] = useState(false);
  const [selectedDevotee, setSelectedDevotee] = useState(null);
  const [devoteeData, setDevoteeData] = useState(initialDevoteeData);
  const [originalDevoteeData, setOriginalDevoteeData] = useState(null);
  const [isDevoteeConfirmed, setIsDevoteeConfirmed] = useState(false);

  // Country code related state
  const [countryList, setCountryList] = useState([]);
  const [currentSelected, setCurrentSelected] = useState(initialCountryCode);

  // Address related state
  const [addressList, setAddressList] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [addressData, setAddressData] = useState(initialAddressData);
  const [originalAddressData, setOriginalAddressData] = useState(null);
  const [isNewAddress, setIsNewAddress] = useState(false);
  const [isAddressConfirmed, setIsAddressConfirmed] = useState(false);
  const [isAddressSkipped, setIsAddressSkipped] = useState(false);

  const currentDevoteeId = selectedDevotee?.devotee_id || devoteeData.devotee_id;

  // Load country codes on mount
  useEffect(() => {
    const loadCountryCodes = async () => {
      try {
        const response = await GetCountryCodeList(token);
        setCountryList(
          response.map((country) => ({
            value: country.country_currency_id,
            label: `${country.country_name}  :  ${country.phone_code}`,
          }))
        );
      } catch (err) {
        setError("Failed to load country codes");
        notify("Failed to load country codes", "error");
      }
    };
    loadCountryCodes();
  }, [userData]);

  /* ------------------------------ Devotee flow ----------------------------- */
  const resetDevoteeState = () => {
    setSelectedDevotee(null);
    setDevoteeData(initialDevoteeData);
    setOriginalDevoteeData(null);
    setCurrentSelected(initialCountryCode);
    setAddressList([]);
    setSelectedAddress(null);
    setIsNewAddress(false);
    setIsAddressConfirmed(false);
    setIsAddressSkipped(false);
    setAddressData(initialAddressData);
    setOriginalAddressData(null);
    setIsDevoteeConfirmed(false);
  };

  // Search uses its own isSearching state (NOT isLoading), so the page does not
  // get the dark full screen overlay on every search.
  const handleDevoteeSearch = async (searchTerm) => {
    const requestId = ++latestSearch.current;

    if (!searchTerm) {
      setDevoteeOptions([]);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    try {
      const response = await FindDevoteeAPI(searchTerm, 1, token);
      if (requestId !== latestSearch.current) return; // an older search, ignore it
      setDevoteeOptions(response?.is_success ? response.data.list : []);
    } catch (err) {
      if (requestId !== latestSearch.current) return;
      setError("Error searching for devotee");
      notify("Error searching for devotee", "error");
      setDevoteeOptions([]);
    } finally {
      if (requestId === latestSearch.current) setIsSearching(false);
    }
  };

  const handleDevoteeSelect = async (devotee) => {
    if (!devotee) {
      resetDevoteeState();
      return;
    }

    setIsLoading(true);
    try {
      const [devoteeDetails, addressDetails] = await Promise.all([
        GetDevoteeDataAPI(devotee.devotee_id, token),
        GetDevoteeAddressAPI(devotee.devotee_id, token),
      ]);

      if (devoteeDetails.is_success) {
        setSelectedDevotee(devotee);
        setDevoteeData(toDevoteeForm(devoteeDetails.data));
        setOriginalDevoteeData(toDevoteeForm(devoteeDetails.data));
        const country = countryList.find((c) => c.value == devoteeDetails.data.country_code);
        if (country) setCurrentSelected(country);
      }
      if (addressDetails.is_success) {
        setAddressList(addressDetails.data.list);
      }
    } catch (err) {
      setError("Error fetching devotee details");
      notify("Error fetching devotee details", "error");
    } finally {
      setIsLoading(false);
    }
  };

  // searchText = what was typed in the search box, used to prefill the new devotee form
  const handleNewDevoteeToggle = (event, searchText = "") => {
    setIsNewDevotee(event.target.checked);
    setIsDevoteeConfirmed(false);
    if (event.target.checked) {
      resetDevoteeState();
      setDevoteeData({ ...initialDevoteeData, ...getPrefillFromSearch(searchText) });
    }
  };

  const handleDevoteeUndo = () => setDevoteeData(originalDevoteeData ?? initialDevoteeData);

  const handleDevoteeSubmit = async (formData, hasChanges) => {
    if (!hasChanges) {
      setIsDevoteeConfirmed(true);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const isEdit = !!selectedDevotee;
      const payload = cleanPayload({
        ...formData,
        country_code: Number(currentSelected.value),
        ...(isEdit && { devotee_id: selectedDevotee.devotee_id }),
        birth_date: flipDate(formData.birth_date),
        anniversary_date: flipDate(formData.anniversary_date),
      });

      const response = isEdit
        ? await EditDevoteDataAPI(payload, token)
        : await AddDevoteeAPI(payload, token);

      if (!response.is_success) {
        notify(response.message || "Failed to save devotee", "error");
        return;
      }

      // The success message is already shown by the API call, so it is not repeated here.
      if (!isEdit) {
        // Keep the form's own (yyyy-mm-dd) date format so the date inputs still render
        const savedDevotee = { ...response.data, ...formData };
        setSelectedDevotee(savedDevotee);
        setDevoteeData(savedDevotee);
        setOriginalDevoteeData(savedDevotee);
        setIsDevoteeConfirmed(true);
        setIsNewAddress(true);
        return;
      }

      setOriginalDevoteeData(formData);
      const devoteeDetails = await GetDevoteeDataAPI(selectedDevotee.devotee_id, token);
      if (devoteeDetails.is_success) {
        setDevoteeData(toDevoteeForm(devoteeDetails.data));
        setIsDevoteeConfirmed(true);
      } else {
        setIsDevoteeConfirmed(false);
        notify(devoteeDetails.message || "Failed to refresh devotee details", "error");
      }
    } catch (err) {
      notify(err.message || "Error saving devotee", "error");
    } finally {
      setIsLoading(false);
    }
  };

  /* ------------------------------ Address flow ----------------------------- */
  const handleAddressSelect = (addressId) => {
    setSelectedAddress(addressId);
    setIsAddressSkipped(false);
    const selected = addressList.find((address) => address.devotee_address_id === addressId);
    if (selected) {
      setAddressData(selected);
      setOriginalAddressData(selected);
      setIsNewAddress(false);
    }
  };

  const resetAddressForm = () => {
    setAddressData({ ...initialAddressData });
    setOriginalAddressData(null);
    setSelectedAddress(null);
    setIsAddressConfirmed(false);
    setIsAddressSkipped(false);
  };

  const handleNewAddressToggle = () => {
    setIsNewAddress(!isNewAddress);
    resetAddressForm();
  };

  const handleAddressUndo = () => {
    if (isNewAddress) {
      resetAddressForm();
    } else {
      setAddressData(originalAddressData);
    }
  };

  // Skip: continue to the amount step without saving / selecting any address
  const handleSkipAddress = () => {
    setSelectedAddress(null);
    setAddressData({ ...initialAddressData });
    setOriginalAddressData(null);
    setIsAddressSkipped(true);
    setIsAddressConfirmed(true);
  };

  const handleAddressSubmit = async (formData, hasChanges) => {
    if (!hasChanges) {
      setIsAddressSkipped(false);
      setIsAddressConfirmed(true);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const isEdit = !!selectedAddress;
      const payload = cleanPayload({ ...formData, devotee_id: currentDevoteeId });

      const response = isEdit
        ? await EditDevoteeAddressAPI(payload, token)
        : await AddAddressAPI(payload, token);

      if (!response.is_success) {
        setError(response.message || "Failed to save address");
        notify(response.message || "Failed to save address", "error");
        setIsAddressConfirmed(false);
        return;
      }

      // The success message is already shown by the API call, so it is not repeated here.
      setSelectedAddress(isEdit ? formData.devotee_address_id : response.data.devotee_address_id);

      const addressDetails = await GetDevoteeAddressAPI(currentDevoteeId, token);
      if (addressDetails.is_success) {
        setAddressList(addressDetails.data.list);
      }
      setIsAddressSkipped(false);
      setIsAddressConfirmed(true);
    } catch (err) {
      setError(err.message || "Error saving address");
      notify(err.message || "Error saving address", "error");
    } finally {
      setIsLoading(false);
    }
  };

  /* ------------------------------ Receipt flow ----------------------------- */
  const handleReceiptSubmit = async (receiptData) => {
    setIsLoading(true);
    try {
      const response = await AddReceiptAPI(receiptData, token);

      if (response.is_success) {
        setIsLoading(false);
        await Swal.fire({
          title: "Success!",
          text: "Receipt sent successfully",
          icon: "success",
          confirmButtonColor: colors.primary.main,
        });
        window.location.reload();
      } else {
        notify(response.message || "Failed to send receipt", "error");
      }
    } catch (err) {
      notify(err.message || "Error sending receipt", "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    // Full width like the other pages (no maxWidth / auto margin)
    <MDBox px={{ xs: 1, md: 3 }} pt={{ xs: 0, md: 1 }} pb={3}>
      <ReceiptHeader />

      {error && (
        <MDTypography color="error" mb={2}>
          {error}
        </MDTypography>
      )}

      {/* Step 1: devotee */}
      <SectionCard
        // step={1}
        title="Devotee"
        subtitle="Search an existing devotee, or add a new one"
        // done={isDevoteeConfirmed}
      >
        <DevoteeSearch
          devoteeOptions={devoteeOptions}
          isLoading={isSearching}
          isNewDevotee={isNewDevotee}
          onSearch={handleDevoteeSearch}
          onDevoteeSelect={handleDevoteeSelect}
          onNewDevoteeToggle={handleNewDevoteeToggle}
        />

        {(isNewDevotee || selectedDevotee) && (
          <DevoteeForm
            devoteeData={devoteeData}
            countryList={countryList}
            currentSelected={currentSelected}
            setCurrentSelected={setCurrentSelected}
            onSubmit={handleDevoteeSubmit}
            onUndoChanges={handleDevoteeUndo}
            isEdit={!!selectedDevotee}
            isConfirmed={isDevoteeConfirmed}
          />
        )}
      </SectionCard>

      {isDevoteeConfirmed && (
        <>
          {/* Step 2: address */}
          <SectionCard
            title="Address"
            subtitle={isAddressSkipped ? "" : "Choose a saved address, add a new one, or skip"}
            done={isAddressConfirmed}
          >
            {!isAddressSkipped && (
              <AddressSearch
                addressList={addressList}
                selectedAddress={selectedAddress}
                onAddressSelect={handleAddressSelect}
                isNewAddress={isNewAddress}
                onNewAddressToggle={handleNewAddressToggle}
                onSkip={handleSkipAddress}
                isAddressDone={isAddressConfirmed}
              />
            )}

            {!isAddressSkipped && (isNewAddress || selectedAddress) && (
              <AddressForm
                addressData={addressData}
                onSubmit={handleAddressSubmit}
                onUndoChanges={handleAddressUndo}
                isEdit={!!selectedAddress}
                isConfirmed={isAddressConfirmed}
              />
            )}

            {isAddressSkipped && (
              <MDTypography variant="button" color="text" display="block">
                Address skipped. You can continue to the amount step.
              </MDTypography>
            )}
          </SectionCard>

          {/* Step 3: amount */}
          {isAddressConfirmed && (
            <SectionCard title="Amount" subtitle="Enter the amount and send the receipt">
              <ReceiptForm
                devoteeId={currentDevoteeId}
                devoteeAddressId={selectedAddress}
                onSubmit={handleReceiptSubmit}
                isLoading={isLoading}
              />
            </SectionCard>
          )}
        </>
      )}

      <Backdrop
        sx={{ color: colors.white.main, zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={isLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </MDBox>
  );
};

export default Receipt;
